import { useState, type FormEvent } from 'react';
import {
  bootstrapAdminEmailSchema,
  SHORT_NAME_MAX,
  shortNameSchema,
  subscriptionDatesProblem,
  type ExposureMode,
  type LockedAfterEnrollment,
  type TenantStatus,
} from '@teachpartner/contracts';
import { ErrorBanner } from './badges';

/** An example only (shown greyed out); the field stays empty unless the school has its own domain. */
const CUSTOM_DOMAIN_EXAMPLE = 'teachpartner.yourdomain.com';

export interface TenantFormValues {
  code: string;
  name: string;
  /** App header, home screen and the welcome email (1-24 characters). */
  shortName: string;
  /** First Tenant Admin: sent to the school once at enrollment, then deleted from central. */
  bootstrapAdminEmail: string;
  customDomain: string;
  exposureMode: ExposureMode;
  tailscaleAddress: string;
  status: TenantStatus;
  plan: string;
  startsOn: string;
  endsOn: string;
  contactName: string;
  contactEmail: string;
}

export const EMPTY_TENANT: TenantFormValues = {
  code: '',
  name: '',
  shortName: '',
  bootstrapAdminEmail: '',
  customDomain: '',
  exposureMode: 'passthrough',
  tailscaleAddress: '',
  status: 'active',
  plan: '',
  startsOn: '',
  endsOn: '',
  contactName: '',
  contactEmail: '',
};

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Form values → API body fields shared by create and update. */
export function toTenantFields(v: TenantFormValues) {
  return {
    name: v.name.trim(),
    shortName: v.shortName.trim(),
    customDomain: orNull(v.customDomain),
    exposureMode: v.exposureMode,
    tailscaleAddress: orNull(v.tailscaleAddress),
    status: v.status,
    subscription: {
      plan: orNull(v.plan),
      startsOn: orNull(v.startsOn),
      endsOn: orNull(v.endsOn),
      contactName: orNull(v.contactName),
      contactEmail: orNull(v.contactEmail),
    },
  };
}

/** Problem with the first Tenant Admin email, or null when it is valid. */
export function adminEmailProblem(value: string): string | null {
  if (value.trim() === '') return 'Enter the first Tenant Admin email.';
  return bootstrapAdminEmailSchema.safeParse(value).success
    ? null
    : 'Enter a valid email address for the first Tenant Admin.';
}

/** Problem with the short name, or null when it is valid. */
export function shortNameProblem(value: string): string | null {
  if (value.trim() === '') return 'Enter a short name.';
  const result = shortNameSchema.safeParse(value);
  if (result.success) return null;
  const reason = result.error.issues[0]?.message ?? 'not valid';
  return `Short name: ${reason} (1-${SHORT_NAME_MAX} characters).`;
}

interface Props {
  readonly initial: TenantFormValues;
  readonly mode: 'create' | 'edit';
  readonly busy: boolean;
  /** Ask for the first Tenant Admin (create, and edit until the school has enrolled). */
  readonly showAdminEmail?: boolean;
  /** Shown read-only (owner decision 2026-10-10: locked once the school has enrolled). */
  readonly locked?: readonly LockedAfterEnrollment[];
  onSubmit(values: TenantFormValues): void;
}

const LOCK_NOTE = 'Locked after enrollment';

export function TenantForm({ initial, mode, busy, showAdminEmail, locked = [], onSubmit }: Props) {
  const [values, setValues] = useState(initial);
  const [problem, setProblem] = useState<string | null>(null);
  const askAdmin = showAdminEmail ?? mode === 'create';
  const set = (key: keyof TenantFormValues) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const issue =
      shortNameProblem(values.shortName) ??
      (askAdmin ? adminEmailProblem(values.bootstrapAdminEmail) : null) ??
      subscriptionDatesProblem(orNull(values.startsOn), orNull(values.endsOn));
    setProblem(issue);
    if (!issue) onSubmit(values);
  };
  const isLocked = (key: keyof TenantFormValues) => (locked as readonly string[]).includes(key);
  const field = (
    key: keyof TenantFormValues,
    label: string,
    type = 'text',
    placeholder?: string,
  ) => (
    <label>
      {label}
      <input
        type={type}
        value={values[key]}
        onChange={set(key)}
        placeholder={isLocked(key) ? undefined : placeholder}
        readOnly={isLocked(key)}
        aria-description={isLocked(key) ? LOCK_NOTE : undefined}
      />
      {isLocked(key) && (
        <small className="muted" aria-hidden="true">
          {LOCK_NOTE}
        </small>
      )}
    </label>
  );

  return (
    <form className="form-grid" onSubmit={submit} aria-label={`${mode} tenant`}>
      {mode === 'create' && (
        <label>
          Code
          <input value={values.code} onChange={set('code')} required pattern="[A-Z0-9]{2,12}" />
        </label>
      )}
      {field('name', 'Name')}
      <label>
        Short name
        <input
          value={values.shortName}
          onChange={set('shortName')}
          maxLength={SHORT_NAME_MAX}
          aria-required="true"
          aria-description="App header and home screen; up to 24 characters."
        />
      </label>
      {askAdmin && (
        <label>
          First Tenant Admin email
          <input
            type="text"
            inputMode="email"
            autoComplete="off"
            aria-required="true"
            aria-description="Sent to the school once when it enrolls, then deleted from central."
            value={values.bootstrapAdminEmail}
            onChange={set('bootstrapAdminEmail')}
          />
        </label>
      )}
      <ErrorBanner message={problem} />
      {field('customDomain', 'Custom domain (optional)', 'text', CUSTOM_DOMAIN_EXAMPLE)}
      <label>
        Exposure
        <select
          value={values.exposureMode}
          onChange={set('exposureMode')}
          disabled={isLocked('exposureMode')}
        >
          <option value="passthrough">SNI passthrough (E2E)</option>
          <option value="cloudflare-tunnel">Cloudflare tunnel (not E2E)</option>
        </select>
        {isLocked('exposureMode') && (
          <small className="muted" aria-hidden="true">
            {LOCK_NOTE}
          </small>
        )}
      </label>
      {field('tailscaleAddress', 'Tailscale address')}
      <label>
        Status
        <select value={values.status} onChange={set('status')}>
          {['active', 'maintenance', 'suspended', 'disabled'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      {field('plan', 'Plan')}
      {field('startsOn', 'Starts on', 'date')}
      {field('endsOn', 'Ends on', 'date')}
      {field('contactName', 'Contact name')}
      {field('contactEmail', 'Contact email', 'email')}
      <button type="submit" disabled={busy}>
        {mode === 'create' ? 'Create tenant' : 'Save changes'}
      </button>
    </form>
  );
}
